import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { confirmDeleteAccount } from '../../../api/authApi';
import { useAuth } from '../../../context/AuthContext';
import { Eyebrow, PageHeading } from '../../../design-system';
import styles from './EmailVerificationPage.module.css';

export const DeleteAccountConfirmationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearAuthState } = useAuth();

  const token = searchParams.get('token') ?? '';
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>(token ? 'idle' : 'error');
  const [message, setMessage] = useState(
    token
      ? 'Please confirm your request to permanently delete this account.'
      : 'This deletion link is missing or invalid.',
  );

  const handleConfirm = async () => {
    if (!token) {
      setStatus('error');
      setMessage('This deletion link is missing or invalid.');
      return;
    }

    setLoading(true);
    try {
      const result = await confirmDeleteAccount(token);
      clearAuthState();
      setStatus('success');
      setMessage(result.detail || 'Your account has been permanently deleted.');
      window.setTimeout(() => navigate('/signup', { replace: true }), 1500);
    } catch (err) {
      setStatus('error');
      setMessage(err instanceof Error ? err.message : 'Failed to confirm account deletion.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.themeToggle}>
      </div>

      <div className={styles.card}>
        <div className={styles.logo}>Policyora</div>

        <svg
          width="52"
          height="52"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--ds-danger)"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginTop: '16px' }}
        >
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M19 6l-.867 12.142A2 2 0 0 1 16.138 20H7.862a2 2 0 0 1-1.995-1.858L5 6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
        </svg>

        <Eyebrow tone="default" dot>ACCOUNT</Eyebrow>
        <PageHeading
          accentWord="deletion"
          accentTone="red"
          size="h2"
          align="center"
          level="h1"
          className={styles.heading}
        >
          Confirm account deletion
        </PageHeading>
        <p className={styles.subtext}>
          This action permanently deletes your account and associated access. It cannot be undone after confirmation.
        </p>

        <div
          className={`${styles.messageBanner} ${
            status === 'error' ? styles.messageBannerError : styles.messageBannerSuccess
          }`}
          role={status === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          <span className={styles.messageText}>{message}</span>
        </div>

        <button
          type="button"
          className={styles.submitButton}
          onClick={handleConfirm}
          disabled={loading || !token || status === 'success'}
        >
          {loading ? 'Deleting…' : status === 'success' ? 'Deletion confirmed' : 'Confirm delete'}
        </button>

        <div className={styles.resendRow}>
          <button type="button" className={styles.resendButton} onClick={() => navigate('/login')}>
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
};
